/**
 * AuthProvider - Centralized Authentication Wrapper
 * 
 * Provides a unified auth interface that works with both:
 * - Real Clerk authentication (when VITE_CLERK_PUBLISHABLE_KEY is set)
 * - Mock development authentication (when key is missing)
 * 
 * This allows the app to work in preview/development environments
 * without requiring Clerk credentials.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { ClerkProvider, useUser as useClerkUser, Protect as ClerkProtect, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { setUser as setErrorTrackingUser } from '@/app/utils/errorTracking';

// Feature mapping removed as logic is handled by Clerk permissions
// export const PLAN_FEATURES...

// Get Clerk key from Vite environment variables
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || '';

// Check if we're in development mode (no Clerk key)
export const IS_DEV_MODE = !CLERK_PUBLISHABLE_KEY || CLERK_PUBLISHABLE_KEY.trim() === '';

/**
 * Mock user for development mode
 */
const MOCK_USER = {
  id: 'dev-user-001',
  emailAddresses: [{ emailAddress: 'dev@example.com' }],
  primaryEmailAddress: { emailAddress: 'dev@example.com' },
  firstName: 'Dev',
  lastName: 'User',
  fullName: 'Dev User',
  imageUrl: '',
};

/**
 * Unified auth context interface
 */
interface AuthContextValue {
  user: {
    id: string;
    email: string;
    name: string;
    imageUrl?: string;
  } | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isDevelopmentMode: boolean;
  has: (params: { permission?: string; plan?: string }) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Development Mode Auth Wrapper
 * Provides mock authentication when Clerk key is not available
 */
function DevelopmentAuthProvider({ children }: { children: ReactNode }) {
  const mockAuthValue: AuthContextValue = {
    user: {
      id: MOCK_USER.id,
      email: MOCK_USER.primaryEmailAddress.emailAddress,
      name: MOCK_USER.fullName,
      imageUrl: MOCK_USER.imageUrl,
    },
    isAuthenticated: true,
    isLoading: false,
    isDevelopmentMode: true,
    has: () => true, // Always allow in dev mode
  };

  // Set mock user in error tracking
  React.useEffect(() => {
    setErrorTrackingUser({
      id: MOCK_USER.id,
      email: MOCK_USER.primaryEmailAddress.emailAddress,
      username: MOCK_USER.fullName,
    });
  }, []);

  return (
    <AuthContext.Provider value={mockAuthValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Production Mode Auth Wrapper
 * Uses real Clerk authentication
 */
function ProductionAuthProvider({ children }: { children: ReactNode }) {
  const { user: clerkUser, isLoaded } = useClerkUser();
  const { has } = useClerkAuth();

  const authValue: AuthContextValue = {
    user: clerkUser ? {
      id: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress || '',
      name: clerkUser.fullName || `${clerkUser.firstName || ''} ${clerkUser.lastName || ''}`.trim() || 'User',
      imageUrl: clerkUser.imageUrl,
    } : null,
    isAuthenticated: !!clerkUser,
    isLoading: !isLoaded,
    isDevelopmentMode: false,
    has: (params) => {
      // Wrapper to handle potential undefined 'has' during loading or if useAuth returns undefined
      if (!has) return false;
      return has(params);
    }
  };

  // Set user in error tracking whenever user changes
  React.useEffect(() => {
    if (clerkUser) {
      setErrorTrackingUser({
        id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress,
        username: clerkUser.fullName || undefined,
      });
    } else {
      setErrorTrackingUser(null);
    }
  }, [clerkUser]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Main AuthProvider Component
 * Automatically chooses between dev mode and production mode
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  if (IS_DEV_MODE) {
    console.log('🔧 Development mode: Using mock authentication');
    return <DevelopmentAuthProvider>{children}</DevelopmentAuthProvider>;
  }

  // Production mode: Use real Clerk
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <ProductionAuthProvider>{children}</ProductionAuthProvider>
    </ClerkProvider>
  );
}

/**
 * Custom hook to access auth context
 * Use this instead of directly calling Clerk APIs
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

/**
 * Safe version of useAuth that returns null if provider is not available
 * Useful for components that might render before providers are ready
 */
export function useAuthSafe(): AuthContextValue | null {
  const context = useContext(AuthContext);
  return context || null;
}

/**
 * HOC to protect routes that require authentication
 * In dev mode, this always allows access
 * In production mode, it redirects to Clerk sign-in
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    if (!isAuthenticated && !IS_DEV_MODE) {
      // In production, redirect to sign-in
      // This would typically use Clerk's RedirectToSignIn component
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-gray-600 mb-4">Please sign in to continue</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
}

/**
 * Wrapper for Clerk's Protect component that handles:
 * - Development mode (always allows access)
 * - Custom plan checking via has({ plan: '...' })
 */
interface ProtectProps {
  children: ReactNode;
  fallback?: ReactNode;
  plan?: string;
  feature?: string;
}

export function Protect({ children, fallback, plan, feature }: ProtectProps) {
  if (IS_DEV_MODE) {
    return <>{children}</>;
  }

  return (
    <ClerkProtect
      fallback={fallback}
      condition={(has) => {
        if (feature) {
           // Check for permission directly in Clerk
           return (has as any)({ permission: feature });
        }

        if (plan) {
          // Cast to any to support custom "plan" property check requested by user
          return (has as any)({ plan });
        }
        return true;
      }}
    >
      {children}
    </ClerkProtect>
  );
}
